import { ParentComponent, createSignal, onCleanup, onMount } from 'solid-js';

interface Props {
  onSee?: () => void;
  onSeen?: () => void;
  onGone?: () => void;
  container?: HTMLElement;
}

export const InView: ParentComponent<Props> = props => {
  const [seen, setSeen] = createSignal(false);
  let ref: HTMLDivElement | undefined;
  let observer: IntersectionObserver;

  onMount(() => {
    if (ref) {
      const options = {
        threshold: 0.5,
        root: props.container || null,
      };
      observer = new IntersectionObserver(onSee, options);
      observer.observe(ref);
      // NOTE: Skip elements that aren't rendered (e.g. display: none) as their zeroed bounds look visible.
      // The observer will pick them up once they are shown.
      if (isRendered(ref) && (isVisible(ref) || isScrolledPast(ref))) {
        see();
      }
    }
  });

  onCleanup(() => {
    if (observer) {
      observer.disconnect();
    }
  });

  const isRendered = (el: HTMLDivElement) => {
    const bounds = el.getBoundingClientRect();
    return bounds.width > 0 || bounds.height > 0;
  };

  const isVisible = (el: HTMLDivElement) => {
    const bounds = el.getBoundingClientRect();
    if (props.container) {
      const containerBounds = props.container.getBoundingClientRect();
      return bounds.top <= containerBounds.bottom && bounds.bottom >= containerBounds.top;
    } else {
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      return bounds.top <= windowHeight && bounds.bottom >= 0;
    }
  };

  const isScrolledPast = (el: HTMLDivElement) => {
    const bounds = el.getBoundingClientRect();
    if (props.container) {
      const containerBounds = props.container.getBoundingClientRect();
      return bounds.bottom < containerBounds.top;
    } else {
      return bounds.bottom < 0;
    }
  };

  const onSee = (entries: IntersectionObserverEntry[]) => {
    if (entries[0] && entries[0].isIntersecting) {
      see();
    } else if (entries[0] && !entries[0].isIntersecting) {
      onGone();
    }
  };

  const onSeen = () => {
    props.onSeen && props.onSeen();
  };

  const onGone = () => {
    if (ref && !isVisible(ref) && seen()) {
      props.onGone && props.onGone();
    }
  };

  const see = () => {
    props.onSee && props.onSee();
    if (!seen()) {
      onSeen();
      setSeen(true);
      if (!props.onSee && !props.onGone && observer) {
        observer.disconnect();
      }
    }
  };

  return <div ref={ref}>{props.children}</div>;
};
